import { Component, AfterViewInit, ViewChildren, QueryList, ElementRef, ViewChild } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { TargetDrawer } from '../TargetDrawer';
import { ActivatedRoute } from '@angular/router';
import { ImportedTargetDefinition } from '../imported-target.model';
import { isDecimalPistolTarget, parseImportedTargetXml } from '../imported-target.parser';

@Component({
  selector: 'app-tarca',
  templateUrl: './tarca.component.html',
  styleUrls: ['./tarca.component.scss']
})
export class TarcaComponent {
  private readonly importedTargetAssignmentsKey = 'tarce.importedTargetAssignments';
  private readonly importedTargetXmlKey = 'tarce.importedTargetXml';
  title = 'Tarce';

  @ViewChildren('targetCanvas') canvasRefs!: QueryList<ElementRef<HTMLCanvasElement>>;
  @ViewChild('targetCanvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;

  shots: any[] = [];
  targetData: any[] = [];
  shotsResults: any[] = [];
  targetID: string | null = null;
  determinedTargetType = '';
  selectedXmlTarget: ImportedTargetDefinition | null = null;
  usesImportedTarget = false;
  importedTarget: ImportedTargetDefinition | null = null;
  selectedShotIndex: number | null = null;

  isSeriesView = true;
  shotsLoaded = false;
  targetDataLoaded = false;
  isTeilerTarget = false;

  private lastDrawnState = { isSeriesView: false, shotsLoaded: false, targetDataLoaded: false };

  constructor(private http: HttpClient, private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.targetID = this.route.snapshot.paramMap.get('id');
    if (!this.targetID) {
      console.warn('No targetID provided. Skipping data load.');
      return;
    }
    this.usesImportedTarget = this.hasImportedTargetAssignment(this.targetID);
    this.selectedXmlTarget = this.usesImportedTarget ? this.loadImportedTarget() : null;
    this.importedTarget = this.selectedXmlTarget;
    if (this.usesImportedTarget && !this.selectedXmlTarget) {
      this.usesImportedTarget = false;
    }
    this.loadImportedTargetAssignment(this.targetID);
    this.shotsLoaded = false;
    this.targetDataLoaded = false;
    this.loadTargetById(this.targetID);
    this.loadShotsForTarget(Number(this.targetID));
    this.getRezultati(Number(this.targetID));
  }

  private hasImportedTargetAssignment(targetID: string): boolean {
    try {
      const assignments = JSON.parse(localStorage.getItem(this.importedTargetAssignmentsKey) || '{}');
      return !!assignments[targetID];
    } catch {
      return false;
    }
  }

  private loadImportedTarget(): ImportedTargetDefinition | null {
    try {
      const xmlText = localStorage.getItem(this.importedTargetXmlKey);
      return xmlText ? parseImportedTargetXml(xmlText) : null;
    } catch (error) {
      console.warn('Uvožene XML tarče ni mogoče uporabiti:', error);
      return null;
    }
  }

  private loadImportedTargetAssignment(targetID: string): void {
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id');
    if (!token || !unitId) return;
    this.http.get<any>(`https://testapi.ovh/api/strelstvo/targets/${targetID}/xml-target`, {
      headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
      params: new HttpParams().set('unit_id', unitId)
    }).subscribe({
      next: assignment => {
        this.selectedXmlTarget = assignment?.definition || null;
        this.importedTarget = this.selectedXmlTarget;
        this.usesImportedTarget = !!this.selectedXmlTarget;
        setTimeout(() => this.tryDrawTarget(), 0);
      },
      error: error => console.error('Error loading imported target assignment:', error)
    });
  }

  ngAfterViewChecked(): void {
    // Only redraw if something relevant changed
    if (
      this.shotsLoaded &&
      this.targetDataLoaded &&
      (
        this.isSeriesView !== this.lastDrawnState.isSeriesView ||
        this.shotsLoaded !== this.lastDrawnState.shotsLoaded ||
        this.targetDataLoaded !== this.lastDrawnState.targetDataLoaded
      )
    ) {
      this.tryDrawTarget();
      this.lastDrawnState = {
        isSeriesView: this.isSeriesView,
        shotsLoaded: this.shotsLoaded,
        targetDataLoaded: this.targetDataLoaded
      };
    }
  }

  getRezultati(targetId: number): void {
    const apiUrl = 'https://testapi.ovh/api/strelstvo/rezultati';
    const params = new HttpParams().set('targetID', targetId.toString());
    const token = localStorage.getItem('access_token');
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;

    this.http.get<any>(apiUrl, { params, headers }).subscribe(
      (result) => {
        this.shotsResults = result ? [result] : [];
        console.log('shotsResults:', this.shotsResults);
      },
      (error) => {
        console.error('Error fetching rezultati:', error);
        this.shotsResults = [];
      }
    );
  }

  toggleView(): void {
    this.isSeriesView = !this.isSeriesView;
    setTimeout(() => this.tryDrawTarget(), 0); // Wait for DOM update
  }

  selectShot(index: number): void {
    this.selectedShotIndex = this.selectedShotIndex === index ? null : index;
    setTimeout(() => this.tryDrawTarget(), 0);
  }

  // Returns an array of valid series for a target
  getValidSeries(target: any): string[] {
    const seriesKeys = ['S10', 'S20', 'S30', 'S40', 'S50', 'S60'];
    return seriesKeys.filter(series => target[series] !== null && target[series] > 0);
  }

  getSeriesLabel(series: string): string {
    return {
      S10: '1. serija',
      S20: '2. serija',
      S30: '3. serija',
      S40: '4. serija',
      S50: '5. serija',
      S60: '6. serija'
    }[series] || series;
  }

  formatShotValue(value: unknown): string {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '-';
    const normalized = this.normalizeDecimalValue(parsed);
    return String(Math.min(10, Math.floor(normalized)));
  }

  formatDecimalShotValue(value: unknown): string {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '-';
    const normalized = this.normalizeDecimalValue(parsed);
    return normalized.toFixed(1);
  }

  private normalizeDecimalValue(value: number): number {
    const targetType = this.normalizeTargetType(this.effectiveTargetType);
    if (['air rifle', 'air rifle teiler'].includes(targetType) && Number.isInteger(value) && Math.abs(value) >= 20) {
      return value / 10;
    }
    return Number.isInteger(value) && Math.abs(value) >= 20 ? value / 10 : value;
  }

  private normalizeTargetType(targetType: unknown): string {
    return String(targetType || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private isDecimalPistolType(targetType: unknown): boolean {
    return /^(?:air )?pistol decimal$/.test(this.normalizeTargetType(targetType));
  }

  private getDecimalShotSource(shot: any): number | null {
    const shotValue = Number(shot?.ShotValue);
    const fullShotValue = Number(shot?.FullShotValue);
    const targetType = this.normalizeTargetType(this.effectiveTargetType);

    if (['pistol', 'pistol decimal', 'air rifle', 'air serial'].includes(targetType)) {
      const distanceMm = this.getCoordinateDistanceMm(shot);
      if (distanceMm !== null) {
        return this.estimateProfileScore(distanceMm, targetType);
      }
    }

    if (targetType === 'air serial' && Number.isFinite(shotValue)) {
      return shotValue;
    }

    if (Number.isFinite(fullShotValue)) {
      return fullShotValue;
    }
    return Number.isFinite(shotValue) ? shotValue : null;
  }

  getShotDecimalValue(shot: any): string {
    const decimalSource = this.getDecimalShotSource(shot);
    return decimalSource !== null ? this.formatDecimalShotValue(decimalSource) : '-';
  }

  private getShotDecimalNumber(shot: any): number | null {
    const decimalSource = this.getDecimalShotSource(shot);
    if (decimalSource === null) return null;
    return this.normalizeDecimalValue(decimalSource);
  }

  getShotTeilerValue(shot: any): string {
    const value = Number(shot?.ShotValue);
    return this.isTeilerDisplay && Number.isFinite(value) ? value.toFixed(0) : '-';
  }

  get isTeilerDisplay(): boolean {
    return this.normalizeTargetType(this.effectiveTargetType).endsWith(' teiler');
  }

  get showDecimalValues(): boolean {
    const targetType = this.normalizeTargetType(this.effectiveTargetType);
    return this.isTeilerDisplay || this.isDecimalPistolType(targetType) || !['air serial', 'pistol'].includes(targetType);
  }

  get isDecimalPistolDisplay(): boolean {
    return this.isDecimalPistolType(this.effectiveTargetType);
  }

  get isDecimalPrimaryDisplay(): boolean {
    return this.isDecimalPistolDisplay || this.normalizeTargetType(this.effectiveTargetType) === 'air rifle';
  }

  formatSeriesValue(value: unknown): string {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '-';
    return parsed.toFixed(1);
  }

  private getShotNumber(shot: any, fallbackIndex: number): number {
    const recordedNumber = Number(shot?.TargetShotZAP ?? shot?.OrderOfShot);
    return Number.isFinite(recordedNumber) && recordedNumber > 0
      ? recordedNumber
      : fallbackIndex + 1;
  }

  getSeriesShots(seriesIndex: number): any[] {
    const firstShotNumber = seriesIndex * 10 + 1;
    const lastShotNumber = firstShotNumber + 9;

    return this.shots
      .map((shot, index) => ({ shot, number: this.getShotNumber(shot, index) }))
      .filter(({ number }) => number >= firstShotNumber && number <= lastShotNumber)
      .sort((left, right) => left.number - right.number)
      .map(({ shot }) => shot);
  }

  getShotIndex(shot: any): number {
    return this.shots.indexOf(shot);
  }

  getSeriesInnerCenters(seriesIndex: number): number {
    return this.getSeriesShots(seriesIndex).filter(shot => shot.inner_center === 'D' || Number(shot.inner_center) > 0).length;
  }

  getSeriesTeilerTotal(seriesIndex: number): string {
    const values = this.getSeriesShots(seriesIndex)
      .map(shot => Number(shot?.ShotValue))
      .filter(value => Number.isFinite(value));
    return values.length ? values.reduce((sum, value) => sum + value, 0).toFixed(0) : '-';
  }

  getMinimumTeilerValue(shots: any[] = this.shots): string {
    const values = shots
      .map(shot => Number(shot?.ShotValue))
      .filter(value => Number.isFinite(value));
    return values.length ? Math.min(...values).toFixed(0) : '-';
  }

  getTotalTeilerValue(): string {
    const values = this.shots
      .map(shot => Number(shot?.ShotValue))
      .filter(value => Number.isFinite(value));
    return values.length ? values.reduce((sum, value) => sum + value, 0).toFixed(0) : '-';
  }

  getSeriesWholeValue(target: any, series: string, seriesIndex: number): string {
    if (this.normalizeTargetType(this.effectiveTargetType) === 'air rifle') {
      const shotValues = this.getSeriesShots(seriesIndex)
        .map(shot => Number(shot?.ShotValue))
        .filter(value => Number.isFinite(value));
      if (shotValues.length) {
        return String(shotValues.reduce((sum, value) => sum + Math.floor(value), 0));
      }

      const targetValue = Number(target?.[series]);
      return Number.isFinite(targetValue) ? String(Math.floor(targetValue)) : '-';
    }

    const values = this.getSeriesShots(seriesIndex)
      .map(shot => this.getShotDecimalNumber(shot))
      .filter((value): value is number => value !== null)
      .map(value => Math.min(10, Math.floor(value)));
    if (values.length) {
      return String(values.reduce((sum, value) => sum + value, 0));
    }

    const targetValue = Number(target?.[series]);
    return Number.isFinite(targetValue) ? String(Math.floor(targetValue)) : '-';
  }

  getTotalInnerCenters(): number {
    return this.shots.filter(shot => shot.inner_center === 'D' || Number(shot.inner_center) > 0).length;
  }

  getTotalShotValue(target: any): string {
    if (this.normalizeTargetType(this.effectiveTargetType) === 'air rifle') {
      const shotValues = this.shots
        .map(shot => Number(shot?.ShotValue))
        .filter(value => Number.isFinite(value));
      if (shotValues.length) {
        return String(shotValues.reduce((sum, value) => sum + Math.floor(value), 0));
      }

      const targetTotal = Number(target?.series_sum);
      if (Number.isFinite(targetTotal)) {
        return String(Math.floor(targetTotal));
      }
    }

    const values = this.shots
      .map(shot => this.getShotDecimalNumber(shot))
      .filter((value): value is number => value !== null)
      .map(value => Math.min(10, Math.floor(value)));
    if (values.length) {
      return String(values.reduce((sum, value) => sum + value, 0));
    }
    const total = Number(target?.series_sum);
    return Number.isFinite(total) ? String(Math.floor(total)) : '-';
  }

  getTotalDecimalShotValue(target: any): string {
    if (this.isDecimalPrimaryDisplay || this.normalizeTargetType(this.effectiveTargetType) === 'air rifle teiler') {
      const targetTotal = Number(target?.series_sum);
      if (Number.isFinite(targetTotal)) {
        return this.formatSeriesValue(targetTotal);
      }
    }

    const values = this.shots
      .map(shot => this.getShotDecimalNumber(shot))
      .filter((value): value is number => value !== null);
    if (values.length) {
      return values.reduce((sum, value) => sum + value, 0).toFixed(1);
    }
    return this.formatSeriesValue(target?.series_sum);
  }

  getSeriesDecimalValue(target: any, series: string, seriesIndex: number): string {
    if (this.isDecimalPrimaryDisplay || this.normalizeTargetType(this.effectiveTargetType) === 'air rifle teiler') {
      const targetValue = Number(target?.[series]);
      if (Number.isFinite(targetValue)) {
        return this.formatSeriesValue(targetValue);
      }
    }

    const values = this.getSeriesShots(seriesIndex)
      .map(shot => this.getShotDecimalNumber(shot))
      .filter((value): value is number => value !== null);
    if (values.length) {
      return values.reduce((sum, value) => sum + value, 0).toFixed(1);
    }
    return this.formatSeriesValue(target[series]);
  }

  async downloadPdf(): Promise<void> {
    if (!this.targetData.length || !this.shots.length) return;

    const [{ jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const target = this.targetData[0];
    const series = this.getValidSeries(target);
    const canvases = this.canvasRefs.toArray().map(ref => ref.nativeElement);

    series.forEach((seriesKey, index) => {
      if (index > 0) pdf.addPage();

      pdf.setTextColor(30, 58, 138);
      pdf.setFontSize(16);
      pdf.text(`Tarca ${target.targetID || ''} - ${this.getSeriesLabel(seriesKey)}`, 14, 14);
      pdf.setTextColor(71, 85, 105);
      pdf.setFontSize(9);
      pdf.text(`Strelec: ${target.member_full_name || '-'}   Tekmovanje: ${target.competition_name || '-'}`, 14, 21);
      const totalText = this.isTeilerDisplay
        ? `Decimalno skupaj: ${this.getTotalDecimalShotValue(target)}   Teiler skupaj: ${this.getTotalTeilerValue()}   Najmanjsi Teiler: ${this.getMinimumTeilerValue()}`
        : this.isDecimalPrimaryDisplay
          ? `Skupaj: ${this.getTotalDecimalShotValue(target)} / ${this.getTotalShotValue(target)}`
        : this.showDecimalValues
          ? `Skupaj: ${this.getTotalShotValue(target)} / ${this.getTotalDecimalShotValue(target)}`
          : `Skupaj: ${this.getTotalShotValue(target)}`;
      pdf.text(totalText, 14, 27);
      pdf.text(`Notranji centri skupaj: ${this.getTotalInnerCenters()}`, 14, 32);

      const canvas = canvases[index] || canvases[0];
      if (canvas) {
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 14, 34, 108, 108);
      }

      const shotRows = this.getSeriesShots(index);
      autoTable(pdf, {
        startY: 38,
        margin: { left: 132, right: 14 },
        head: [this.isTeilerDisplay
          ? ['Strel', 'Decimalna vrednost', 'Teiler', 'Notranji center']
          : this.isDecimalPrimaryDisplay
            ? ['Strel', 'Decimalno', 'Celi', 'Notranji center']
          : this.showDecimalValues
            ? ['Strel', 'Vrednost', 'Decimalna vrednost', 'Notranji center']
            : ['Strel', 'Vrednost', 'Notranji center']],
        body: shotRows.map(shot => this.isTeilerDisplay
          ? [
            shot.TargetShotZAP ?? '-',
            this.getShotDecimalValue(shot),
            this.getShotTeilerValue(shot),
            shot.inner_center === 'D' ? 'Da' : 'Ne'
          ]
          : this.isDecimalPrimaryDisplay
            ? [
              shot.TargetShotZAP ?? '-',
              this.getShotDecimalValue(shot),
              this.formatShotValue(this.getShotDecimalValue(shot)),
              shot.inner_center === 'D' ? 'Da' : 'Ne'
            ]
          : this.showDecimalValues
            ? [
              shot.TargetShotZAP ?? '-',
              this.formatShotValue(this.getShotDecimalValue(shot)),
              this.getShotDecimalValue(shot),
              shot.inner_center === 'D' ? 'Da' : 'Ne'
            ]
            : [
              shot.TargetShotZAP ?? '-',
              this.formatShotValue(this.getShotDecimalValue(shot)),
              shot.inner_center === 'D' ? 'Da' : 'Ne'
            ]),
        foot: [this.isTeilerDisplay
          ? ['Skupaj', this.getSeriesDecimalValue(target, seriesKey, index), this.getSeriesTeilerTotal(index), `${this.getSeriesInnerCenters(index)}`]
          : this.isDecimalPrimaryDisplay
            ? ['Skupaj', this.getSeriesDecimalValue(target, seriesKey, index), this.getSeriesWholeValue(target, seriesKey, index), `${this.getSeriesInnerCenters(index)}`]
          : this.showDecimalValues
            ? ['Skupaj', this.getSeriesWholeValue(target, seriesKey, index), this.getSeriesDecimalValue(target, seriesKey, index), `${this.getSeriesInnerCenters(index)}`]
            : ['Skupaj', this.getSeriesWholeValue(target, seriesKey, index), `${this.getSeriesInnerCenters(index)}`]],
        theme: 'grid',
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 9 },
        footStyles: { fillColor: [239, 246, 255], textColor: [30, 58, 138], fontStyle: 'bold' },
        bodyStyles: { fontSize: 9, textColor: [51, 65, 85] },
        alternateRowStyles: { fillColor: [248, 250, 252] }
      });
    });

    pdf.save(`tarca-${target.targetID || 'porocilo'}.pdf`);
  }

  loadShotsForTarget(targetID: number): void {
    const apiUrl = 'https://testapi.ovh/api/strelstvo/shots';
    const params = new HttpParams().set('targetID', targetID.toString());
    const token = localStorage.getItem('access_token');
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;

    this.http.get<any[]>(apiUrl, { params, headers }).subscribe(
      (shotsData) => {
        this.shots = Array.isArray(shotsData) ? shotsData : [];
        const firstShot = this.shots
          .map((shot, index) => ({ shot, number: this.getShotNumber(shot, index) }))
          .sort((left, right) => left.number - right.number)[0]?.shot;
        this.isTeilerTarget = Number(firstShot?.ShotValue) > 110;
        this.shotsLoaded = true;
        console.log(`Shots fetched for target ID ${targetID}:`, this.shots);
      },
      (error) => {
        console.error(`Error fetching shots for target ID ${targetID}:`, error);
        this.shots = [];
        this.shotsLoaded = false;
      }
    );
  }

  loadTargetById(targetID: string): void {
    const apiUrl = 'https://testapi.ovh/api/strelstvo/targets';
    const params = new HttpParams()
      .set('targetID', targetID)
      .set('unit_id', localStorage.getItem('unit_id') || '1');
    const token = localStorage.getItem('access_token');
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;

    this.http.get<any[]>(apiUrl, { params, headers }).subscribe(
      (targetsData) => {
        this.targetData = Array.isArray(targetsData) ? targetsData : [];
        this.determinedTargetType = String(this.targetData[0]?.target_type || '').trim().toLowerCase();
        this.targetDataLoaded = true;
        console.log('Targets fetched:', this.targetData);
      },
      (error) => {
        console.error('Error fetching targets:', error);
        this.targetData = [];
        this.targetDataLoaded = false;
      }
    );
  }

  tryDrawTarget(): void {
    if (
      this.shotsLoaded &&
      this.targetDataLoaded &&
      this.shots.length > 0 &&
      this.targetData.length > 0 &&
      this.canvasRefs.length > 0
    ) {
      const canvases = this.canvasRefs.toArray().map(ref => ref.nativeElement);
      const targetType = this.effectiveTargetType;

      if (this.isSeriesView) {
        // Series view: only the first target, one canvas per series
        this.drawTarget(this.shots, targetType, canvases);
      } else {
        // Default view: only one canvas, show all shots
        this.drawAllShots(this.shots, targetType, canvases[0]);
      }
    }
  }

  get effectiveTargetType(): string {
    const rawType = this.determinedTargetType || this.selectedXmlTarget?.profile || this.inferTargetProfile() || 'air rifle';
    const baseType = this.isDecimalPistolType(rawType) ? 'pistol decimal' : rawType;
    const scoringType = this.selectedXmlTarget && isDecimalPistolTarget(this.selectedXmlTarget)
      ? 'pistol decimal'
      : baseType;
    const normalizedType = this.normalizeTargetType(scoringType);
    return this.isTeilerTarget && !normalizedType.endsWith(' teiler')
      ? `${scoringType} teiler`
      : scoringType;
  }

  private inferTargetProfile(): 'pistol' | 'air rifle' | 'air serial' | null {
    const firstShot = this.shots
      .map((shot, index) => ({ shot, number: this.getShotNumber(shot, index) }))
      .sort((left, right) => left.number - right.number)[0]?.shot;
    if (!firstShot) return null;

    const fullShotValue = Number(firstShot?.FullShotValue);
    const shotValue = Number(firstShot?.ShotValue);
    const decimalScore = Number.isFinite(fullShotValue)
      ? this.normalizeDecimalValue(fullShotValue)
      : Number.isFinite(shotValue) ? this.normalizeDecimalValue(shotValue) : null;
    const distanceMm = this.getCoordinateDistanceMm(firstShot);
    if (decimalScore === null || distanceMm === null) return null;

    const profiles: Array<{ name: 'pistol' | 'air rifle' | 'air serial'; rings: number[] }> = [
      { name: 'pistol', rings: [155.5, 139.5, 123.5, 107.5, 91.5, 75.5, 59.5, 43.5, 27.5, 11.5] },
      { name: 'air rifle', rings: [45.5, 40.5, 35.5, 30.5, 25.5, 20.5, 15.5, 10.5, 5.5, 0.5] },
      { name: 'air serial', rings: [83, 74, 65, 56, 47, 38, 29, 20, 11, 2] }
    ];

    return profiles
      .map(profile => ({ name: profile.name, difference: Math.abs(this.estimateRingScore(distanceMm, profile.rings) - decimalScore) }))
      .sort((left, right) => left.difference - right.difference)[0]?.name || null;
  }

  private getCoordinateDistanceMm(shot: any): number | null {
    const xCoord = Number(shot?.XCoord);
    const yCoord = Number(shot?.YCoord);
    if (!Number.isFinite(xCoord) || !Number.isFinite(yCoord)) return null;
    return Math.sqrt(xCoord ** 2 + yCoord ** 2) * 1000;
  }

  private estimateRingScore(distanceMm: number, ringDiameters: number[]): number {
    for (let ringNumber = 10; ringNumber >= 1; ringNumber--) {
      const outerRadius = ringDiameters[ringNumber - 1] / 2;
      const innerRadius = ringNumber < 10 ? ringDiameters[ringNumber] / 2 : 0;
      if (distanceMm <= outerRadius) {
        const ringWidth = outerRadius - innerRadius;
        if (ringWidth <= 0) return ringNumber;
        const positionFromOuter = (outerRadius - distanceMm) / ringWidth;
        return ringNumber + 0.9 * Math.max(0, Math.min(1, positionFromOuter));
      }
    }
    return 0;
  }

  private estimateProfileScore(distanceMm: number, targetType: string): number {
    const ringDiameters = targetType === 'pistol'
      || targetType === 'pistol decimal'
      ? [155.5, 139.5, 123.5, 107.5, 91.5, 75.5, 59.5, 43.5, 27.5, 11.5]
      : targetType === 'air serial'
        ? [83, 74, 65, 56, 47, 38, 29, 20, 11, 2]
        : [45.5, 40.5, 35.5, 30.5, 25.5, 20.5, 15.5, 10.5, 5.5, 0.5];
    const pelletRadiusMm = 2.25;
    if (distanceMm > ringDiameters[0] / 2 + pelletRadiusMm) return 0;

    for (let ringNumber = 10; ringNumber >= 1; ringNumber--) {
      const outerRadius = ringDiameters[ringNumber - 1] / 2 + pelletRadiusMm;
      const innerRadius = ringNumber < 10
        ? ringDiameters[ringNumber] / 2 + pelletRadiusMm
        : 0;
      if (distanceMm <= outerRadius) {
        const ringWidth = outerRadius - innerRadius;
        if (ringWidth <= 0) return ringNumber;
        const positionFromOuter = (outerRadius - distanceMm) / ringWidth;
        return ringNumber + 0.9 * Math.max(0, Math.min(1, positionFromOuter));
      }
    }
    return 0;
  }

  getTargetTypeLabel(targetType: string | null | undefined): string {
    switch ((targetType || 'air rifle').trim().toLowerCase()) {
      case 'pistol':
        return 'Pištola';
      case 'pistol decimal':
        return 'Zračna pištola 40 (decimalno)';
      case 'pistol teiler':
        return 'Pištola Teiler';
      case 'air rifle teiler':
        return 'Zračna puška Teiler';
      case 'air serial':
        return 'Zračna serijska puška';
      case 'air rifle':
      default:
        return 'Zračna puška';
    }
  }

  drawTarget(shots: any[], targetType: string, canvases: HTMLCanvasElement[]): void {
    canvases.forEach((canvas, index) => {
      const seriesShots = this.getSeriesShots(index);
      const selectedShot = this.selectedShotIndex === null ? null : this.shots[this.selectedShotIndex];
      const visibleShots = selectedShot
        ? seriesShots.includes(selectedShot) ? [selectedShot] : []
        : seriesShots;
      const selectedSeriesIndex = selectedShot && visibleShots.length ? 0 : null;

      // Clear the canvas before drawing
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (this.selectedXmlTarget) {
        TargetDrawer.drawImportedXmlTarget(canvas, this.selectedXmlTarget, visibleShots, 0, visibleShots.length - 1, selectedSeriesIndex);
      } else if (targetType.startsWith('pistol')) {
        TargetDrawer.drawPistolTarget(canvas, visibleShots, 1.5, 0, visibleShots.length - 1, selectedSeriesIndex);
      } else if (targetType === 'air serial' || targetType === 'air serial teiler') {
        TargetDrawer.drawSerialAirRifleTarget(canvas, visibleShots, 2.5, 0, visibleShots.length - 1, selectedSeriesIndex);
      } else if (targetType === 'air rifle' || targetType === 'air rifle teiler') {
        TargetDrawer.drawFullAirRifleTarget(canvas, visibleShots, 4, 0, visibleShots.length - 1, selectedSeriesIndex);
      } else {
        console.warn(`Unknown target type: ${targetType}`);
      }
    });
  }

  drawAllShots(shots: any[], targetType: string, canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    const selectedShot = this.selectedShotIndex === null ? null : this.shots[this.selectedShotIndex];
    const visibleShots = selectedShot ? [selectedShot] : shots;
    const selectedIndex = selectedShot ? 0 : null;

    if (this.selectedXmlTarget) {
      TargetDrawer.drawImportedXmlTarget(canvas, this.selectedXmlTarget, visibleShots, 0, visibleShots.length - 1, selectedIndex);
    } else if (targetType.startsWith('pistol')) {
      TargetDrawer.drawPistolTarget(canvas, visibleShots, 1, 0, visibleShots.length - 1, selectedIndex);
    } else if (targetType === 'air serial' || targetType === 'air serial teiler') {
      TargetDrawer.drawSerialAirRifleTarget(canvas, visibleShots, 2.5, 0, visibleShots.length - 1, selectedIndex);
    } else if (targetType === 'air rifle' || targetType === 'air rifle teiler') {
      TargetDrawer.drawFullAirRifleTarget(canvas, visibleShots, 4, 0, visibleShots.length - 1, selectedIndex);
    } else {
      console.warn(`Unknown target type: ${targetType}`);
    }
  }
}

